import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';

import Button from '../../../components/Button';
import { formatPostedAtClockTime, SHORT_SHA_LENGTH } from '../releaseLogic';

import ReleaseBodyMarkdown from './ReleaseBodyMarkdown';

import type { AdminGetCurrentReleaseDraftQuery } from '../__generated__/ReleasesScene.gql';
import type { IAnnounceJustPosted } from '../releaseLogic';

interface ICurrentDraftCardProps {
  draft: NonNullable<AdminGetCurrentReleaseDraftQuery['adminGetCurrentReleaseDraft']>;
  /**
   * Re-post handler for the "Re-post Slack" button — bound to
   * `handleAnnounce` upstream because it goes through the same
   * idempotent-retry path (existing draft + same head sha → re-post).
   */
  onRepostSlack: () => void;
  isReposting: boolean;
  /**
   * `view in Slack` permalink shown next to the action header after the
   * operator clicks Re-post. Cleared on the next refetch.
   */
  justPosted: IAnnounceJustPosted | null;
}

/**
 * Primary "what's open right now" card. Renders the MOPS draft as the
 * release manager will see it once published — sticky header with tag /
 * sha / drafted-by, the LLM-rendered body as markdown, and a placeholder
 * **Publish** affordance (disabled; the backend `adminPublishReleaseDraft`
 * mutation exists, but this card is not yet wired to call it — operators
 * publish via the github.com draft page).
 *
 * When this card is rendered, the "Pending release" preview is hidden:
 * the operator already committed to a SHA when they announced, so the
 * preview at HEAD is no longer the actionable state — the draft is.
 */
const CurrentDraftCard = ({ draft, onRepostSlack, isReposting, justPosted }: ICurrentDraftCardProps) => (
  <section aria-label="Current release draft" className="border-border-primary border">
    {/* Solid `bg-app` matches the body — same elevated-sticky pattern as the preview card. */}
    <div className="bg-app border-border-primary sticky top-0 z-10 border-b px-4 py-3 shadow-lg shadow-black/40">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex flex-col">
          {/*
            The header colour here is deliberately the warning-amber accent
            (not the default primary white) so it visually separates from a
            *published* release at a glance. The "DRAFT" prefix + amber tag
            value reads as "this isn't done yet — one more click to
            publish". Mirrors the orange `<code>` chip palette so the warm
            tones cluster as one visual language for "operator action
            required" content.
          */}
          <span className="text-fg-warning-primary text-[10px] font-semibold uppercase tracking-widest">
            ⚠ Unpublished draft
          </span>
          <span className="text-fg-warning-primary font-mono text-2xl font-semibold">{draft.tag}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Target SHA</span>
          <span className="text-fg-secondary font-mono text-sm">{draft.targetSha.slice(0, SHORT_SHA_LENGTH)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Drafted by</span>
          <span className="text-fg-secondary font-mono text-sm">
            {draft.draftedByEmail} · {formatTimeAgo(draft.draftedAt)}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
          {justPosted && (
            <span className="text-fg-secondary text-xs">
              Posted at {formatPostedAtClockTime(justPosted.postedAt)}
              {justPosted.slackUrl && (
                <>
                  {' — '}
                  <a
                    href={justPosted.slackUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
                  >
                    view in Slack
                  </a>
                </>
              )}
            </span>
          )}
          <Button color="secondary" size="sm" isLoading={isReposting} onClick={() => onRepostSlack()}>
            Re-post Slack
          </Button>
          {/*
            Disabled placeholder. The backend `adminPublishReleaseDraft`
            mutation exists, but this button is not yet wired to call it —
            operators publish/discard via the github.com draft page in the
            meantime. The placeholder stays so the operator can see where
            the action will land.
          */}
          <Tooltip title="Not wired yet — for now, publish or discard via the GitHub draft page." placement="top">
            <TooltipTrigger>
              <span className="inline-block">
                <Button color="primary" size="sm" isDisabled>
                  Publish release
                </Button>
              </span>
            </TooltipTrigger>
          </Tooltip>
          <a
            href={draft.htmlUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-fg-brand-primary hover:text-fg-brand-primary_alt text-xs underline underline-offset-2"
          >
            View draft on github.com →
          </a>
        </div>
      </div>
    </div>
    <div className="px-4 py-4">
      <ReleaseBodyMarkdown body={draft.body} />
    </div>
  </section>
);

export default CurrentDraftCard;
